import { SubmitHandler, useForm } from "react-hook-form";
import strings from "../../../i18n/i18nStrings";
import { TLocation } from "../../../types/Locations";
import Modal from "../modal/Modal";
import { GoogleAddressInputWrapper } from "../../formComponents/GoogleAddressInput";
import { toast } from "react-toastify";
import { useState } from "react";
import ConfirmationDialog from "../ConfirmationDialog";

const ERROR_TOAST_STYLE = {
  className: "bg-red-600 text-zinc-50",
  progressClassName: "opacity-90 [background:#fafafa]",
};

const addLocationSuccessNotification = () =>
  toast(
    "Location added successfully"
  );

const editLocationSuccessNotification = () =>
  toast(
    "Location updated successfully"
  );

const addEditLocationFailureNotification = () =>
  toast(
    "Something went wrong adding / editing the location. Try again later.",
    ERROR_TOAST_STYLE
  );

const deleteLocationSuccessNotification = () =>
  toast(
    "Location deleted successfully"
  );

const deleteLocationFailureNotification = () =>
  toast(
    "Something went wrong deleting the location. Try again later.",
    ERROR_TOAST_STYLE
  );

const LocationModal = ({
  createLocation,
  onClose,
  selectedLocation,
  updateLocationById,
  deleteLocation
}: {
  createLocation: (data: TLocation) => Promise<void>
  onClose: () => void;
  selectedLocation?: TLocation;
  updateLocationById: (locationId: string, data: Partial<TLocation>) => Promise<void>
  deleteLocation?: (locationId: string) => Promise<void>
}) => {
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<TLocation>({
    defaultValues: {
      name: selectedLocation?.name || "",
      address: selectedLocation?.address || "",
      lat: selectedLocation?.lat || 0,
      lon: selectedLocation?.lon || 0,
      color: selectedLocation?.color || "#3B82F6"
    }
  });
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  
  // Watch the lat/lon values to ensure they're set
  const watchedLat = watch("lat");
  const watchedLon = watch("lon");

  const onSubmit: SubmitHandler<TLocation> = async (data) => {
    if (data.name.length === 0) {
      return
    }

    // Validate that coordinates are set - check for valid non-zero coordinates
    const hasValidCoordinates = data.lat && data.lon && 
                               data.lat !== 0 && data.lon !== 0 &&
                               !isNaN(data.lat) && !isNaN(data.lon);
    
    if (!hasValidCoordinates) {
      toast("Please select an address from the dropdown to set location coordinates", ERROR_TOAST_STYLE);
      return;
    }

    try {
      if (selectedLocation?._id) {
        await updateLocationById(selectedLocation._id, data)

        editLocationSuccessNotification()
      } else {
        await createLocation(data);

        addLocationSuccessNotification()
      }

      onClose()
    } catch (err) {
      console.log(err)
      addEditLocationFailureNotification()
    }
  };

  const handleDeleteLocation = async () => {
    if (!selectedLocation?._id || !deleteLocation) {
      return;
    }

    try {
      await deleteLocation(selectedLocation._id);
      deleteLocationSuccessNotification();
      onClose();
    } catch (err) {
      console.error('Delete location error:', err);
      deleteLocationFailureNotification();
    }
    setShowDeleteConfirmation(false);
  };

  return (
    <>
      <Modal title={selectedLocation?._id ? 'Edit Location' : 'Add Location'} onClose={onClose} expanded={false}>
        <>
        <p className="text-sm font-medium text-gray-900 mb-4">
          {selectedLocation?._id ? strings.locations.modal.editLocationDescription : strings.locations.modal.addLocationDescription}
        </p>

        <form className="mb-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="w-full mb-4">
            <label
              htmlFor="location"
              className="block text-left text-xs font-bold text-gray-700 uppercase tracking-wider mb-1"
            >
              {strings.locations.modal.locationName}
            </label>

            <input
              type="text"
              id="location"
              {...register("name", { required: true })}
              className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base md:text-sm"
              placeholder={strings.locations.modal.locationPlaceholder}
            />

            {errors.name && (
              <span className="text-red-500 text-xs mt-1 block mb-2">
                This field is required
              </span>
            )}
          </div>

          <div className="w-full mb-4">
            <label
              htmlFor="address"
              className="block text-left text-xs font-bold text-gray-700 uppercase tracking-wider mb-1"
            >
              {strings.locations.modal.address}
            </label>

            {/* <input
              type="text"
              id="address"
              className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder={strings.locations.modal.addressPlaceholder}
            /> */}
            {/* <GoogleAddressInput
              register={register}
              setValue={setValue}
              name="address"
              placeholder="123 Main St."
            /> */}

            <GoogleAddressInputWrapper
              control={control}
              setValue={(key: keyof TLocation, value: string) => setValue(key, value)}
            />
            
            <p className="text-xs text-gray-500 mt-1">
              Please select an address from the dropdown to automatically set location coordinates
              {watchedLat && watchedLon && watchedLat !== 0 && watchedLon !== 0 && (
                <span className="text-green-600 ml-2">✓ Coordinates set</span>
              )}
            </p>

            <input type="hidden" {...register("lat")} />
            <input type="hidden" {...register("lon")} />

            {errors.address && (
              <span className="text-red-500 text-xs mt-1 block  mb-2">
                This field is required
              </span>
            )}
          </div>

          <div className="w-full mb-4">
            <label
              htmlFor="color"
              className="block text-left text-xs font-bold text-gray-700 uppercase tracking-wider mb-1"
            >
              Location Color
            </label>
            <input
              type="color"
              id="color"
              {...register("color")}
              className="w-full text-base md:text-sm text-gray-900 bg-gray-100 rounded-md px-3 py-2 min-h-10"
            />
          </div>

          <div className="mt-4 flex justify-between">
            <div>
              {selectedLocation?._id && deleteLocation && (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirmation(true)}
                  className="px-4 py-2 text-sm font-semibold underline text-red-700 hover:text-red-800"
                >
                  Delete this Location
                </button>
              )}
            </div>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="mr-3 px-4 py-2 text-sm font-semibold underline text-blue-700 hover:text-blue-800"
              >
                {strings.common.cancel}
              </button>
              <button
                type="submit"
                className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-xs font-bold uppercase tracking-wider text-white bg-blue-700 disabled:bg-slate-300`}
                disabled={!isDirty}
              >
                {selectedLocation?._id ? strings.locations.modal.editLocation : strings.locations.modal.addLocation}
              </button>
            </div>
          </div>
        </form>
        {/* <div className="mb-4">
          <h3 className="px-0 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
            {strings.locations.modal.existingLocations}
          </h3>
          <div className="max-h-36 overflow-auto">
            {locations.length ? (
              locations.map((location, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between mb-5"
                >
                  <div className="flex items-center">
                    <NameCircle name={location.name} index={index} />
                    <input
                      type="text"
                      value={location.name}
                      // onChange={(e) => onEditLocation(e, index)}
                      className="ml-2 border-b border-gray-300 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div className="flex grow justify-between ml-5">
                    <span className="text-sm font-semibold mr-6 lowercase">
                      {location.devices?.length === 1
                        ? strings.locations.oneDevice
                        : strings.locations.multiDevices(
                            location.devices?.length || 0
                          )}
                    </span>
                    {!location.devices?.length && (
                      <button
                        // onClick={() => onRemoveLocation(index)}
                        className="text-sm font-semibold underline text-red-700 hover:text-red-800"
                      >
                        {strings.locations.modal.removeLocation}
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-left text-sm font-bold text-gray-500">
                <span>{strings.locations.modal.noLocations}</span>
              </div>
            )}
          </div>
        </div> */}
        <div className="flex justify-end">
          {/* <button
            onClick={onClose}
            className="mr-3 px-4 py-2 text-sm font-semibold underline text-gray-700 hover:text-gray-800"
          >
            {strings.common.cancel}
          </button>
          <button
            onClick={onUpdateLocations}
            className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-xs font-bold uppercase tracking-wider text-white ${
              !hasEditedLocation
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-blue-700 hover:bg-blue-800"
            }`}
          >
            {strings.common.save}
          </button> */}
        </div>
        </>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteLocation}
        title="Delete Location"
        message={`Are you sure you want to delete "${selectedLocation?.name || 'this location'}"? All devices assigned to this location will be unassigned.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default LocationModal;
